import type { ExportQuality } from '@shared/plans';
import { el, clear, setClass } from './dom';
import { icon } from './Icons';
import { compareSlider } from './EnhancePanel';
import { describeExport, isCapped, toExportBlob } from '@/capture/ExportQuality';
import type { ImageSize } from '@/capture/ImageSpec';
import type { PhotoStorage, ShotImage, StoredShot } from '@/capture/PhotoStorage';

/**
 * Contact sheet of saved shots.
 *
 * "Recall" restores the camera *and* the environment the shot was taken under, so a frame can be
 * retaken after the planting changes — which is the reason to save a shot at all rather than just
 * download a PNG. Enhanced variants hang off their parent shot with a before/after slider, so a
 * render is always seen next to the geometry it came from.
 *
 * **Downloads carry the plan's export ceiling** (phase 6.5), and only downloads do: what is on
 * screen, what was rendered, and what it cost are identical on every plan. When the ceiling is not
 * doing any work the button stays a real anchor, so right-click → Save link as keeps working; when
 * it is, the resize has to happen first and the anchor becomes a button that says the size it will
 * produce. See `ExportQuality`.
 */

export interface PhotoGalleryDeps {
  store: PhotoStorage;
  onRecall: (shot: StoredShot) => void;
  onEnhance: (shot: StoredShot) => void;
  onClose: () => void;
  /**
   * The plan's export ceiling. Omitted when there is no account to have a plan, which is the same
   * as no ceiling — see `ExportQuality` for why that is deliberate rather than a gap.
   */
  exportQuality?: () => ExportQuality | null;
}

export class PhotoGallery {
  readonly root: HTMLElement;

  private shots: StoredShot[] = [];
  private expandedId: string | null = null;
  private readonly grid: HTMLElement;
  private readonly summary: HTMLElement;

  constructor(private readonly deps: PhotoGalleryDeps) {
    this.grid = el('div', { class: 'lg-gallery__grid' });
    this.summary = el('span', { class: 'lg-gallery__summary' });

    this.root = el(
      'aside',
      { class: 'lg-gallery', attrs: { 'aria-label': 'Saved shots' } },
      el(
        'header',
        { class: 'lg-gallery__head' },
        el('h2', { class: 'lg-gallery__title', text: 'Shots' }),
        this.summary,
        el('button', {
          class: 'lg-iconbtn',
          html: icon('close', 16),
          title: 'Close gallery',
          attrs: { type: 'button', 'aria-label': 'Close gallery' },
          on: { click: () => this.deps.onClose() },
        }),
      ),
      this.grid,
    );
  }

  /** Reload from the store and redraw. */
  async refresh(): Promise<void> {
    this.shots = await this.deps.store.list();
    this.render();
  }

  private render(): void {
    clear(this.grid);

    const variantCount = this.shots.reduce((total, shot) => total + shot.variants.length, 0);
    this.summary.textContent =
      this.shots.length === 0
        ? ''
        : `${this.shots.length} shot${this.shots.length === 1 ? '' : 's'}` +
          (variantCount > 0 ? ` · ${variantCount} enhanced` : '') +
          (this.deps.store.persistent ? '' : ' · not saved to disk');

    if (this.shots.length === 0) {
      this.grid.appendChild(
        el('p', {
          class: 'lg-gallery__empty',
          text: 'No shots yet. Frame the garden and press the shutter.',
        }),
      );
      return;
    }

    for (const shot of this.shots) {
      this.grid.appendChild(this.renderShot(shot));
    }
  }

  private renderShot(shot: StoredShot): HTMLElement {
    const expanded = this.expandedId === shot.id;
    const thumbUrl = shot.thumbnail.url;

    const card = el(
      'article',
      { class: 'lg-shot' },
      el(
        'button',
        {
          class: 'lg-shot__thumb',
          attrs: { type: 'button', title: 'Recall this frame — restores the camera and the date' },
          on: { click: () => this.deps.onRecall(shot) },
        },
        el('img', { attrs: { src: thumbUrl, alt: shot.title, loading: 'lazy' } }),
        shot.variants.length > 0 &&
          el('span', { class: 'lg-shot__badge', text: String(shot.variants.length) }),
      ),
      el(
        'div',
        { class: 'lg-shot__meta' },
        el('span', { class: 'lg-shot__title', text: shot.title }),
        el('span', { class: 'lg-shot__sub', text: this.describe(shot) }),
      ),
      el(
        'div',
        { class: 'lg-shot__actions' },
        el('button', {
          class: 'lg-iconbtn',
          html: icon('sparkle', 15),
          title: 'Enhance this shot',
          attrs: { type: 'button', 'aria-label': `Enhance ${shot.title}` },
          on: { click: () => this.deps.onEnhance(shot) },
        }),
        this.downloadControl(shot.beauty, shot.size, `${sanitise(shot.title)}.png`, {
          title: 'Download the original render',
          label: `Download ${shot.title}`,
        }),
        el('button', {
          class: 'lg-iconbtn',
          html: icon('layers', 15),
          title: 'Show the species and depth maps sent to the AI',
          attrs: { type: 'button', 'aria-pressed': expanded ? 'true' : 'false' },
          on: {
            click: () => {
              this.expandedId = expanded ? null : shot.id;
              this.render();
            },
          },
        }),
        el('button', {
          class: 'lg-iconbtn lg-iconbtn--danger',
          html: icon('delete', 15),
          title: 'Delete shot',
          attrs: { type: 'button', 'aria-label': `Delete ${shot.title}` },
          on: {
            click: () => {
              void this.deps.store.remove(shot.id).then(() => this.refresh());
            },
          },
        }),
      ),
    );

    setClass(card, 'is-expanded', expanded);
    if (expanded) card.appendChild(this.renderPasses(shot));
    if (shot.variants.length > 0) card.appendChild(this.renderVariants(shot));
    return card;
  }

  /** The two auxiliary passes, so the grounding sent to the model is inspectable rather than magic. */
  private renderPasses(shot: StoredShot): HTMLElement {
    const legend = el('div', { class: 'lg-shot__legend' });
    for (const entry of shot.manifest.legend) {
      legend.appendChild(
        el(
          'span',
          { class: 'lg-shot__legenditem' },
          el('span', { class: 'lg-shot__swatch', style: { background: entry.keyColor } }),
          el('span', { text: `${entry.botanicalName} · ${entry.count}` }),
        ),
      );
    }

    return el(
      'div',
      { class: 'lg-shot__passes' },
      el(
        'div',
        { class: 'lg-shot__pass' },
        el('img', {
          attrs: { src: shot.idMap.url, alt: 'Species map' },
        }),
        el('span', { text: 'Species map' }),
      ),
      el(
        'div',
        { class: 'lg-shot__pass' },
        el('img', {
          attrs: { src: shot.depth.url, alt: 'Depth map' },
        }),
        el('span', { text: 'Depth' }),
      ),
      legend,
    );
  }

  private renderVariants(shot: StoredShot): HTMLElement {
    const list = el('div', { class: 'lg-shot__variants' });
    const beforeUrl = shot.beauty.url;

    for (const variant of shot.variants) {
      const afterUrl = variant.image.url;
      list.appendChild(
        el(
          'div',
          { class: 'lg-variant' },
          compareSlider(beforeUrl, afterUrl),
          el(
            'div',
            { class: 'lg-variant__row' },
            el('span', { class: 'lg-variant__label', text: variant.presetLabel }),
            this.downloadControl(
              variant.image,
              variant.size,
              `${sanitise(shot.title)}-${variant.presetId}.png`,
              { title: 'Download enhanced render', label: 'Download enhanced render' },
            ),
            el('button', {
              class: 'lg-iconbtn lg-iconbtn--danger',
              html: icon('delete', 15),
              title: 'Delete this render',
              attrs: { type: 'button', 'aria-label': 'Delete enhanced render' },
              on: {
                click: () => {
                  void this.deps.store
                    .removeVariant(shot.id, variant.id)
                    .then(() => this.refresh());
                },
              },
            }),
          ),
        ),
      );
    }
    return list;
  }

  /**
   * A download control for one image, in whichever of its two forms applies.
   *
   * An anchor when the plan's ceiling would not change the file: it is the cheaper path, and it is
   * the one where right-click → Save link as, drag-to-desktop and open-in-new-tab all behave the
   * way a browser's download does. A button when the image has to be resized first, because there
   * is no href to give until the work is done — and because that is exactly the case where the
   * control owes the user a sentence about the size they are getting.
   */
  private downloadControl(
    image: ShotImage,
    size: ImageSize,
    filename: string,
    text: { title: string; label: string },
  ): HTMLElement {
    const quality = this.deps.exportQuality?.() ?? null;
    if (quality === null || !isCapped(size, quality)) {
      return el('a', {
        class: 'lg-iconbtn',
        html: icon('download', 15),
        title: text.title,
        attrs: { href: image.url, download: filename, 'aria-label': text.label },
      });
    }

    const capped = describeExport(size, quality);
    return el('button', {
      class: 'lg-iconbtn',
      html: icon('download', 15),
      title: `${text.title} · ${capped}`,
      attrs: { type: 'button', 'aria-label': `${text.label} · ${capped}` },
      on: {
        click: (event) => {
          const button = event.currentTarget as HTMLButtonElement;
          button.disabled = true;
          void this.saveResized(image, size, quality, filename).finally(() => {
            button.disabled = false;
          });
        },
      },
    });
  }

  private async saveResized(
    image: ShotImage,
    size: ImageSize,
    quality: ExportQuality,
    filename: string,
  ): Promise<void> {
    const blob = await toExportBlob(await image.blob(), size, quality);
    // This URL is minted here and revoked here, unlike everything else the gallery shows: it is
    // over a blob the store has never seen, so there is nothing else that could own its lifetime.
    const url = URL.createObjectURL(blob);
    const anchor = el('a', {
      attrs: { href: url, download: filename },
      style: { display: 'none' },
    });
    this.root.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }

  private describe(shot: StoredShot): string {
    const { environment } = shot.manifest;
    const plants = shot.manifest.visiblePlantCount;
    return `${environment.seasonLabel} · ${environment.timeLabel} · ${plants} plant${plants === 1 ? '' : 's'}`;
  }

  /**
   * The gallery no longer mints or revokes object URLs.
   *
   * Every image it displays comes from `ShotImage.url`, whose lifetime belongs to the store — which
   * is the only thing that knows whether the URL is an object URL over a local blob or an
   * authenticated path into an account. That also fixed a leak this class used to have: it created
   * a fresh object URL for every pass on every re-render and only revoked them on dispose, so
   * expanding and collapsing a card twenty times pinned twenty copies of each image.
   */
  dispose(): void {
    this.root.remove();
  }
}

function sanitise(title: string): string {
  return title.replace(/[^\w-]+/g, '-').replace(/^-+|-+$/g, '') || 'shot';
}
